import {Icon} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useRef, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import * as NavConstants from 'constants/NavConstants';
import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ChatDraftStatus, createChatWithDraftMessage} from 'llm/chat-library/ChatActions';
import {openExistingChat} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {ChatTypeByAgentType, isAgentTypeEnabled} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import APIUtil from 'util/APIUtil';
import {typeToName} from 'util/NameUtil';

type StartChatVariant = 'button' | 'input';

export interface StartChatInputProps {
  itemId: string;
  agentType: AgentType;
  variant: StartChatVariant;
  className?: string;
  extraAnalyticsProperties?: Record<string, unknown>;
}

export default function StartChatInput({
  itemId,
  agentType,
  variant,
  className,
  extraAnalyticsProperties,
}: StartChatInputProps): JSX.Element | null {
  if (!isAgentTypeEnabled(agentType)) {
    return null;
  }

  switch (variant) {
    case 'button':
      return (
        <CollapsibleInput
          itemId={itemId}
          agentType={agentType}
          variant={variant}
          className={className}
          extraAnalyticsProperties={extraAnalyticsProperties}
        />
      );
    case 'input':
      return (
        <InputField
          itemId={itemId}
          agentType={agentType}
          variant={variant}
          className={className}
          extraAnalyticsProperties={extraAnalyticsProperties}
        />
      );
    default:
      return null;
  }
}

interface CollapsibleInputProps extends StartChatInputProps {
  variant: 'button';
}

function CollapsibleInput({
  itemId,
  agentType,
  variant,
  className,
  extraAnalyticsProperties,
}: CollapsibleInputProps): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const collapseTimerRef = useRef<number>();

  useEffect(
    () => () => {
      clearTimeout(collapseTimerRef.current);
    },
    []
  );

  function collapse(): void {
    if (isAnimatingOut) {
      return;
    }
    setIsAnimatingOut(true);
    collapseTimerRef.current = window.setTimeout(() => {
      setIsAnimatingOut(false);
      setIsExpanded(false);
    }, ANIMATION_MS);
  }

  if (!isExpanded && !isAnimatingOut) {
    return (
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        size={ButtonSize.SMALL}
        icon={<Icon icon="sparkle" className={css(styles.sparkleIcon)} />}
        text="Ask"
        aria-label={`Ask about this ${typeToName(APIUtil.apiIdToType(itemId))}`}
        onClick={() => {
          setIsExpanded(true);
        }}
        className={className}
        dataTestAttrs={{component: 'StartChatInput', element: 'button'}}
      />
    );
  }

  return (
    <InputField
      autoFocus={true}
      itemId={itemId}
      agentType={agentType}
      variant={variant}
      className={classNames(
        css(isAnimatingOut ? styles.collapseAnimation : styles.expandAnimation),
        className
      )}
      extraAnalyticsProperties={extraAnalyticsProperties}
      onBlurEmpty={collapse}
      onEscape={collapse}
      onSubmitComplete={collapse}
    />
  );
}

interface InputFieldProps {
  autoFocus?: boolean;
  className?: string;
  itemId: string;
  agentType: AgentType;
  variant: StartChatVariant;
  extraAnalyticsProperties?: Record<string, unknown>;
  onBlurEmpty?: () => void;
  onEscape?: () => void;
  onSubmitComplete?: () => void;
}

function InputField({
  autoFocus,
  className,
  itemId,
  agentType,
  onBlurEmpty,
  onEscape,
  onSubmitComplete,
  variant,
  extraAnalyticsProperties,
}: InputFieldProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [question, setQuestion] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const modelType = APIUtil.apiIdToType(itemId);
  const itemTypeName = typeToName(modelType);

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
  }, [autoFocus]);

  async function handleSubmit(): Promise<void> {
    const trimmed = question.trim();
    if (!trimmed || isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    try {
      await submitQuestion(trimmed, itemId, agentType, variant, extraAnalyticsProperties);
      setQuestion('');
      onSubmitComplete?.();
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent): void {
    if (e.key === 'Escape') {
      setQuestion('');
      onEscape?.();
    } else if (e.key === 'Enter') {
      handleSubmit();
    }
  }

  function handleBlur(): void {
    if (!question.trim()) {
      onBlurEmpty?.();
    }
  }

  return (
    <div
      className={classNames(css(styles.inputWrapper), className)}
      data-test-component="StartChatInput"
      data-test-element="inputWrapper"
    >
      <Icon icon="sparkle" className={css(styles.inputIcon)} />
      <input
        ref={inputRef}
        className={css(styles.input)}
        type="text"
        placeholder={`Ask about this ${itemTypeName}...`}
        aria-label={`Ask about this ${itemTypeName}`}
        value={question}
        disabled={isSubmitting}
        onChange={(e) => {
          setQuestion(e.target.value);
        }}
        onFocus={() => {
          trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.FOCUSED_START_CHAT_INPUT, {
            itemId,
            modelType,
            variant,
            ...extraAnalyticsProperties,
          });
        }}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        data-test-component="StartChatInput"
        data-test-element="input"
      />
    </div>
  );
}

async function submitQuestion(
  question: string,
  itemId: string,
  agentType: AgentType,
  variant: StartChatVariant,
  extraAnalyticsProperties?: Record<string, unknown>
): Promise<void> {
  const chatType = ChatTypeByAgentType[agentType];
  const {chatSessionId} = await createChatWithDraftMessage(chatType, {
    createParams: {contextIds: [itemId]},
    draft: {
      status: ChatDraftStatus.DRAFT,
      message: {text: question, selectedFiles: []},
      settings: {agentType, publicDataEnabled: false},
    },
  });

  openExistingChat(chatSessionId);
  SidepanelActions.openSection(NavConstants.SECTIONS.AI);

  const modelType = APIUtil.apiIdToType(itemId);
  trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.SUBMITTED_START_CHAT_QUESTION, {
    itemId,
    modelType,
    variant,
    chatSessionId,
    ...extraAnalyticsProperties,
  });
}

// Approximate widths used for the expand/collapse animation.
// Button (icon + "Ask" + padding + border) ≈ 60px.
// InputField wrapper (icon + input + padding + border) = 238px.
const COLLAPSED_WIDTH = 60;
const EXPANDED_WIDTH = 238;
const ANIMATION_MS = 200;

const expandKeyframes = {
  from: {width: COLLAPSED_WIDTH},
  to: {width: EXPANDED_WIDTH},
};

const collapseKeyframes = {
  from: {width: EXPANDED_WIDTH},
  to: {width: COLLAPSED_WIDTH},
};

const styles = StyleSheet.create({
  sparkleIcon: {
    color: Colors.PURPLE,
    flexShrink: 0,
    height: 14,
    width: 14,
  },
  expandAnimation: {
    animationName: expandKeyframes,
    animationDuration: `${ANIMATION_MS}ms`,
    animationTimingFunction: 'ease-out',
    overflow: 'hidden',
  },
  collapseAnimation: {
    animationName: collapseKeyframes,
    animationDuration: `${ANIMATION_MS}ms`,
    animationTimingFunction: 'ease-in',
    animationFillMode: 'forwards',
    overflow: 'hidden',
  },
  inputWrapper: {
    alignItems: 'center',
    backgroundColor: Colors.WHITE,
    border: `1px solid ${Colors.GRAY_DARK}`,
    borderRadius: 4,
    display: 'inline-flex',
    height: 28,
    padding: '0 8px',
    verticalAlign: 'middle',
    ':focus-within': {
      borderColor: Colors.PURPLE,
    },
  },
  inputIcon: {
    color: Colors.PURPLE,
    flexShrink: 0,
    height: 14,
    marginRight: 6,
    width: 14,
  },
  input: {
    background: 'transparent',
    border: 'none',
    color: Colors.NEAR_BLACK,
    fontSize: 13,
    outline: 'none',
    width: 200,
    '::placeholder': {
      color: Colors.SILVER,
    },
  },
});
